import React, { Component } from 'react'
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { Route, Switch, Redirect } from 'react-router-dom';
import { withRouter } from 'react-router'
import { withTranslation } from 'react-i18next';
import i18n from 'cityads-v3/i18n';
import { prepareLanguage } from 'cityads-v3/i18n/utils';
import classNames from 'classnames';
import loadable from '@loadable/component'
import withHooks from 'cityads-v3/decorators/withHooks'
import { useThemeChanger, useBrowserThemeListener } from 'cityads-v3/hooks/theme'

import { ThemeProvider as MuiThemeProvider, createGenerateClassName, StylesProvider } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment from 'moment';
import MomentUtils from "@date-io/moment";
import { requireMomentLocale } from 'cityads-v3/utils/dateUtils';

import { AuthChecker, AuthReferralSetter } from 'cityads-v3/components/Auth';
import { NavigationCounter, GTMUserIdentifier } from 'cityads-v3/components/Counters';
import TemporaryRedirect from 'cityads-v3/components/Router/TemporaryRedirect';
import SupportWidget from 'cityads-v3/components/SupportWidget/SupportWidget';
import LanguageListener from 'cityads-v3/components/LanguageListener';
import Balaclava from 'cityads-v3/components/Balaclava'
import CookiePolicy from 'cityads-v3/components/CookiePolicy/CookiePolicy'
import Menu from 'cityads-v3/components/Menu/Menu'
import IsBannedUser from 'cityads-v3/components/IsBannedUser/IsBannedUser'
import Header from 'cityads-v3/components/Header/Header'
import Footer from 'cityads-v3/components/Footer/Footer'

import Main from 'cityads-v3/containers/main/Main';
import NotFound from 'cityads-v3/containers/misc/NotFound';
import WelcomeForm from 'cityads-v3/containers/welcomeform/WelcomeForm';
import AppFabContainer from './AppFabContainer';
import AppReduce from './AppReduce';
import Loading from './Loading';
import OldUrls from './OldUrls';

import { getList as getCurrencies } from 'cityads-v3/actions/currency';
import { loadUserBalance } from 'cityads-v3/actions/auth';
import { onLoadGetShowMenuSetting } from 'cityads-v3/actions/menu';

import { removeNode } from 'cityads-v3/utils/htmlUtils';

import classes from './App.scss';

const generateClassName = createGenerateClassName();

const ManagerContainer = loadable(() => import(/* webpackChunkName: "manager" */'cityads-v3/containers/Manager/Manager'), {
  fallback: <Loading />,
});

const AboutContainer = loadable(() => import(/* webpackChunkName: "about" */'cityads-v3/containers/About/About'), {
  fallback: <Loading />,
});

const NewsContainer = loadable(() => import(/* webpackChunkName: "news" */'cityads-v3/containers/news/News'), {
  fallback: <Loading />,
});

const AdvTypeTrafficContainer = loadable(() => import(/* webpackChunkName: "AdvTypeTraffic" */'cityads-v3/containers/AdvTypeTraffic/AdvTypeTraffic'), {
  fallback: <Loading />,
});

const WebmasterContainer = loadable(() => import(/* webpackChunkName: "webmaster" */'cityads-v3/containers/webmaster/Webmaster'), {
  fallback: <Loading />,
});

const DevTestContainer = loadable(() => import(/* webpackChunkName: "devTest" */'cityads-v3/containers/DevTest/DevTest'), {
  fallback: <Loading />,
});

const HomeContainer = loadable(() => import(/* webpackChunkName: "homeContainer" */'cityads-v3/containers/Home/Home'), {
  fallback: <Loading />,
});

const DEFAULT_HEADER_PARAMS = {
  showHeader: true,
  showFooter: true,

  backlink    : null,
  backlinkText: null,

  title: null,

  tabValue: false,
  tabs    : [],

  menuShow : false,
  menuValue: false,
  menu     : [],

  showFab: true,
};

@withHooks(props => {
  useBrowserThemeListener()
  return {
    ...props,
    themeChanger: useThemeChanger()
  }
})
@withRouter
@connect(state => ({
  language: state.language.language,
  menuDocked: state.menu.docked,
  isMobile: state.app.isMobile,
  isLoggedIn: state.auth.isLoggedIn,
  creditsUrl: state.auth.creditsUrl,
  isLoadedUserBalance: state.auth.isLoadedUserBalance,
  redirectUrl: state.auth.redirect_url,
  needWelcomeForm: state.welcomeform.need_welcome_form,
  leftMenuIsVisible: state.menu.isVisible,
}))
@withTranslation(['main'], { i18n, wait: !!window })
export default class AppContent extends Component {
  static propTypes = {
    dispatch:             PropTypes.func.isRequired,
    location:             PropTypes.any.isRequired,
    sheetsRegistry:       PropTypes.any,
    sheetsManager:        PropTypes.any,
    language:             PropTypes.string,
    menuDocked:           PropTypes.bool,
    creditsUrl:           PropTypes.string,
    isLoadedUserBalance:  PropTypes.bool,
    redirectUrl:          PropTypes.string,
    needWelcomeForm:      PropTypes.bool,
  };

  UNSAFE_componentWillMount() {
    const lang = prepareLanguage(this.props.language);

    requireMomentLocale(lang);
    moment.locale(lang);
  }

  componentDidMount() {
    const { dispatch, isLoadedUserBalance, creditsUrl } = this.props;

    this.cleanUpServerSideJss();
    dispatch(getCurrencies());
    dispatch(onLoadGetShowMenuSetting());

    if(!isLoadedUserBalance && creditsUrl) {
      dispatch(loadUserBalance(creditsUrl));
    }
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const { dispatch, history, language, creditsUrl, redirectUrl } = newProps;

    if (this.props.language !== language) {
      const lang = prepareLanguage(language);

      requireMomentLocale(lang);
      moment.locale(lang);
    }

    if (this.props.creditsUrl !== creditsUrl) {
      dispatch(loadUserBalance(creditsUrl));
    }

    if (this.props.redirectUrl !== redirectUrl) {
      history.push(redirectUrl);
    }
  }

  cleanUpServerSideJss() {
    removeNode(document.getElementById('jss-server-side'));
  }

  helmet() {
    return {
      defaultTitle  : 'CityAds Media',
      titleTemplate : '%s | CityAds Media',
      htmlAttributes: {
        lang: this.props.i18n.language,
        'data-theme': this.props.themeChanger.themeName
      },
    };
  }

  render() {
    const { menuDocked, isMobile, isLoggedIn, leftMenuIsVisible } = this.props;
    const theme = this.props.themeChanger.theme;

    return (
      <StylesProvider
        injectFirst = {true}
        generateClassName = {this.props.generateClassName || generateClassName}
      >
        <MuiThemeProvider theme={theme}>
          <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale={this.props.language}>
            <AppReduce />

            <AuthChecker />

            <AuthReferralSetter />

            <NavigationCounter />
            <GTMUserIdentifier />
            <SupportWidget />
            <LanguageListener />
            {/* <Agreement /> */}

            <Helmet {...this.helmet()} />
            <Balaclava {...DEFAULT_HEADER_PARAMS} defaults={DEFAULT_HEADER_PARAMS} />

            <div className="container">
              <Menu/>
              <div className={classNames({
                [classes.appContent]: true,
                [classes.appContentMenuHidden]: !leftMenuIsVisible,
                'ca-left-menu-docked': menuDocked && !isMobile,
                [classes.isMobile]: isMobile || (!isLoggedIn && !isMobile),
              })}>
                <Header className={classes.header} />

                <div id="content" className={classes.contentArea}>
                  <OldUrls />

                  <Switch>

                    <Route exact path="/" render={() => (
                      <Redirect to="/main" />
                    )}/>
                    <Route exact strict path="/main/" render={() => (
                      <Redirect to="/main" />
                    )}/>

                    <Route path="/main/manager" component={ManagerContainer} />

                    <Route path="/main/welcome" component={WelcomeForm} />
                    {isLoggedIn && this.props.needWelcomeForm && (
                      <TemporaryRedirect to="/main/welcome" />
                    )}

                    <Route path="/main/about" component={AboutContainer} />
                    <Route path="/main/news/:type?" component={NewsContainer} />
                    <Route path="/main/advTypeTraffic/:formId" component={AdvTypeTrafficContainer} />
                    <Route path="/main/devTest" component={DevTestContainer} />
                    <Route path="/main/home" component={HomeContainer} />
                    <Route path="/main" component={Main} />

                    <Route path="/publisher" component={WebmasterContainer} />


                    <Route component={NotFound} />

                  </Switch>
                </div>

                <Footer className={classes.footer} />
              </div>
            </div>

            <AppFabContainer />
            <CookiePolicy />
            <IsBannedUser />
          </MuiPickersUtilsProvider>
        </MuiThemeProvider>
      </StylesProvider>
    );
  }
}

